// pages/ProductsPage.tsx
import { useState, useMemo } from "react";
import { Plus, Search, MoreVertical, Package, Filter, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

import MenuModal from "../../../components/ui/MenuModal";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import PageLayout from "../../../layouts/PageListLayout";
import FlexContainer from "../../../components/ui/FlexContainer";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import { useAuth, useProducts } from "@cabukaka/hooks";
import ProductsList from "../../../components/features/products/ProductsList";

export default function ProductsPage() {
  const navigate = useNavigate();
  const { user } = useAuth()
  const role = user?.role
  const { products, deleteProduct, deleteManyProducts } = useProducts();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedStockStatus, setSelectedStockStatus] = useState<string>("");
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
  const [productToDelete, setProductToDelete] = useState<any | null>(null);
  const [itemsToDelete, setItemsToDelete] = useState<string[] | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Fonction pour déterminer le statut du stock
  const getStockStatus = (stock: number) => {
    if (stock <= 0) return "rupture";
    if (stock <= 10) return "faible";
    return "normal";
  };

  // Récupérer les catégories uniques
  const categoriesOptions = useMemo(() => {
    const cats = new Set(products.map(p => p.category?.name).filter(Boolean));
    return Array.from(cats);
  }, [products]);

  // Filtrage des produits
  const filteredProducts = useMemo(() => {
    let result = products;

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      result = result.filter(p =>
        p.name?.toLowerCase().includes(searchLower) ||
        p.category?.name?.toLowerCase().includes(searchLower)
      );
    }

    if (selectedCategory) {
      result = result.filter(p => p.category?.name === selectedCategory);
    }

    if (selectedStockStatus) {
      result = result.filter(p => getStockStatus(p.stock || 0) === selectedStockStatus);
    }

    return result;
  }, [products, searchTerm, selectedCategory, selectedStockStatus]);

  const stats = useMemo(() => ({
    total: products.length,
    totalValue: products.reduce((acc, p) => acc + ((p.price || 0) * (p.stock || 0)), 0),
    outOfStock: products.filter(p => (p.stock || 0) <= 0).length
  }), [products]);


  const handleDelete = async () => {
    if (!productToDelete?.id) return;
    setIsDeleting(true);
    try {
      await deleteProduct(productToDelete.id);
      setProductToDelete(null);
      alertSuccess("Produit supprimé avec succès");
    } catch (err) {
      alertError("Erreur lors de la suppression");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteMany = async () => {
    if (!itemsToDelete || itemsToDelete.length === 0) return;
    try {
      await deleteManyProducts(itemsToDelete);
      setItemsToDelete(null);
      alertSuccess(`${itemsToDelete.length} produit(s) supprimé(s)`);
    } catch (err) {
      alertError("Erreur lors de la suppression multiple");
    }
  };

  const clearFilters = () => {
    setSelectedCategory("");
    setSelectedStockStatus("");
    setSearchTerm("");
  };

  const hasActiveFilters = selectedCategory || selectedStockStatus || searchTerm;

  const stockStatusOptions = [
    { value: "rupture", label: "Rupture de stock" },
    { value: "faible", label: "Stock faible (≤ 10)" },
    { value: "normal", label: "Stock normal" }
  ];



  return (
    <PageLayout
      title="Gestion des produits"
      description={`${filteredProducts.length} produit${filteredProducts.length > 1 ? 's' : ''} affiché${filteredProducts.length > 1 ? 's' : ''}`}
      actions={
        <FlexContainer className="animate-fade-in-up w-full">
          <button
            onClick={() => { navigate(`/${role}/products/new`); }}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-primary to-primary/80 text-white rounded-xl"
          >
            <Plus size={18} /> Ajouter
          </button>



          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all ${showFilters ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            <Filter size={18} /> Filtrer
            {hasActiveFilters && <span className="ml-1 w-2 h-2 bg-red-500 rounded-full" />}
          </button>
        </FlexContainer>
      }

    >
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5">
          <p className="text-xs font-semibold text-blue-700/70">Total produits</p>
          <p className="text-2xl font-bold text-blue-700">{stats.total}</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-5">
          <p className="text-xs font-semibold text-green-700/70">Valeur du stock</p>
          <p className="text-2xl font-bold text-green-700">{stats.totalValue.toLocaleString('fr-FR')} FCFA</p>
        </div>
        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-5">
          <p className="text-xs font-semibold text-red-700/70">Rupture de stock</p>
          <p className="text-2xl font-bold text-red-700">{stats.outOfStock}</p>
        </div>
      </div>

      {/* Recherche */}
      <div className="relative mb-4">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Rechercher un produit..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
        />
      </div>

      {/* Filtres */}
      {showFilters && (
        <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-700">Filtres</h3>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1">
                <X size={14} /> Effacer tout
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="">Toutes les catégories</option>
                {categoriesOptions.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Statut du stock</label>
              <select
                value={selectedStockStatus}
                onChange={(e) => setSelectedStockStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="">Tous les statuts</option>
                {stockStatusOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Liste */}
      <div>
        <ProductsList
          products={filteredProducts}
          selectable={true}
          onAction={(p) => setSelectedProduct(p)}
          selectActions={[{
            label: "Supprimer",
            onClick: (selected) => setItemsToDelete(selected.map(i => i.id)),
            className: "bg-red-600 text-white"
          }]}
        />
      </div>

      {/* Menu modal */}
      {selectedProduct && (
        <MenuModal
          title={selectedProduct.name}
          isOpen={!!selectedProduct}
          onClose={() => setSelectedProduct(null)}
          icon={<Package className="text-primary" size={20} />}
          menu={[
            { label: "Voir détails", icon: Search, onClick: () => navigate(`/${role}/products/details?id=${selectedProduct.id}`, { state: selectedProduct }) },
            { label: "Modifier", icon: Package, onClick: () => navigate(`/${role}/products/update?id=${selectedProduct.id}`, { state: selectedProduct }) },
            { label: "Supprimer", icon: MoreVertical, onClick: () => { setProductToDelete(selectedProduct); setSelectedProduct(null); } }
          ]}
        />
      )}

      {/* Modals suppression */}
      <DeleteConfirmationModal
        isOpen={!!productToDelete}
        onClose={() => setProductToDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer le produit"
        message={`Supprimer "${productToDelete?.name}" ?`}
        confirmText={isDeleting ? "Suppression..." : "Supprimer"}
      />
      <DeleteConfirmationModal
        isOpen={!!itemsToDelete && itemsToDelete.length > 0}
        onClose={() => setItemsToDelete(null)}
        onConfirm={handleDeleteMany}
        title="Suppression multiple"
        message={`Supprimer ${itemsToDelete?.length} produit(s) ?`}
      />
    </PageLayout>
  );
}